/* oxlint-disable react-hooks/rules-of-hooks */
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import PriceRowLoyaltyPointsPromoPill from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsPromoPill'
import PriceRowLoyaltyPointsLoadingSkeleton from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsLoadingSkeleton'
import { useAppSelector } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import determineLoyaltyBonusPointsCampaignType from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import recalculatePointsEarnAfterBurn from 'loyalty/lib/pointsCalculation/recalculatePointsEarnAfterBurn'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import type { ComponentProps } from 'react'
import { useContext, useMemo } from 'react'
import * as React from 'react'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeover.constants'
import GeoContext from 'contexts/geoContext'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { SOCIETE_DOUBLE_POINTS_TAKEOVER_ID } from 'takeovers/societeDoublePointsTakeover/constants/societeDoublePointsTakeoverConstants'
import { useIntl } from 'react-intl'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import styled from 'styled-components'

type InheritedPriceRowLoyaltyPointsProps = Pick<ComponentProps<typeof PriceRowLoyaltyPoints>, 'onInfoIconClick' | 'calculationType' | 'horizontalAlign'>

interface Props extends InheritedPriceRowLoyaltyPointsProps {
  calculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined
  /**
   * @default 'offer'
   */
  estimateScope?: 'offer' | 'selectedOption'
  /**
   * @default 'S'
   */
  size?: React.ComponentProps<typeof PriceRowLoyaltyPoints>['size']
  /**
   * @default false
   */
  hideStatusCredits?: boolean
  /**
   * @default false
   */
  hidePromoPill?: boolean
  offer?: App.Offer | App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary | App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary | App.ExperienceOffer | App.CruiseOffer
  /**
   * When set, scales earned points and status credits based on the remaining
   * cash after points are applied. If the price is fully covered, the
   * component is hidden.
   */
  pointsToApply?: number
  /** Full burn price returned by the burn API for the displayed price. */
  burnPriceInPoints?: number
}

const Container = styled.div`
  max-width: 100%;
  width: 100%;
`

function _LoyaltyPointsEarn(props: Props) {
  const {
    calculationRequests,
    size = 'S',
    hideStatusCredits = false,
    hidePromoPill = false,
    calculationType,
    estimateScope = 'offer',
    horizontalAlign,
    offer,
    onInfoIconClick,
    pointsToApply,
    burnPriceInPoints,
  } = props
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)
  const siteTakeover = useCurrentSiteTakeover({ offer })
  const triplePointsCampaign = siteTakeover?.id === triplePointsTakeoverId
  const doublePointsCampaign = siteTakeover?.id === SOCIETE_DOUBLE_POINTS_TAKEOVER_ID

  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const shouldHideStatusCreditsAndNights = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const shouldHideStatusCredits = hideStatusCredits || shouldHideStatusCreditsAndNights

  const { totalPointsCalculation, pointsCalculations } = useLoyaltyPointsEarnCalculator(calculationRequests)

  const initial = totalPointsCalculation?.initial
  const fetching = !!totalPointsCalculation?.fetching
  const err = totalPointsCalculation?.error
  const points = totalPointsCalculation?.data?.points ?? 0
  const pointsEarnRatio = totalPointsCalculation?.data?.roundedPointsEarnRatio ?? 0
  const isOfferLevelEstimate = estimateScope === 'offer'
  const campaignTypeFromCalculation = useMemo(() =>
    totalPointsCalculation?.data ? determineLoyaltyBonusPointsCampaignType(totalPointsCalculation.data) : [],
  [totalPointsCalculation?.data])

  const bonusPointsCampaignTypes = useMemo(() => {
    // Return all campaigns to display multiple pills
    if (campaignTypeFromCalculation?.length > 0) return campaignTypeFromCalculation
    const estimatedCampaigns: Array<App.LoyaltyBonusPointsCampaignType> = []
    if (isOfferLevelEstimate && calculationType === 'estimate' && offer && triplePointsCampaign) {
      estimatedCampaigns.push('triple')
    }
    if (isOfferLevelEstimate && calculationType === 'estimate' && offer && doublePointsCampaign) {
      estimatedCampaigns.push('double_status_credits')
    }
    return estimatedCampaigns.length > 0 ? estimatedCampaigns : undefined
  }, [campaignTypeFromCalculation, isOfferLevelEstimate, calculationType, offer, triplePointsCampaign, doublePointsCampaign])
  const selectPackagesCaption = intl.formatMessage({
    id: 'nmCr9qgPG_3dNxjEz62W',
    defaultMessage: '(select packages)',
    description: 'Trailing caption when points estimate requires user to select packages (e.g. for hotel offers)',
  })
  const selectDeparturesCaption = intl.formatMessage({
    id: 'UQDUzlBMq__nR4qpj45D',
    defaultMessage: '(select departures)',
    description: 'Trailing caption when points estimate requires user to select departures (e.g. for tour offers)',
  })
  const trailingCaption = useMemo<string | undefined>(() => {
    if (isOfferLevelEstimate && !campaignTypeFromCalculation?.length && calculationType === 'estimate' && offer && triplePointsCampaign) {
      switch (offer.type) {
        case 'hotel':
        case 'tactical_ao_hotel':
        case 'bedbank_hotel':
          return selectPackagesCaption
        case 'tour':
        case 'direct_tour':
        case 'partner_tour':
        case 'connection_tour':
        case 'cruise':
          return selectDeparturesCaption
      }
    }
  }, [isOfferLevelEstimate, calculationType, offer, campaignTypeFromCalculation, triplePointsCampaign, selectPackagesCaption, selectDeparturesCaption])
  const burnAdjustedTotals = useMemo(() => {
    if (!pointsToApply || pointsToApply <= 0 || !burnPriceInPoints || burnPriceInPoints <= 0) return undefined

    const completedCalculations = nonNullable(pointsCalculations.map(pointsCalculation => pointsCalculation?.data))
    if (!completedCalculations.length) return undefined

    return recalculatePointsEarnAfterBurn(completedCalculations, pointsToApply, burnPriceInPoints)
  }, [burnPriceInPoints, pointsCalculations, pointsToApply])

  const displayedPoints = burnAdjustedTotals?.points ?? points
  const displayedStatusCredits = burnAdjustedTotals
    ? burnAdjustedTotals.statusCredits + burnAdjustedTotals.bonusStatusCredits
    : (totalPointsCalculation?.data?.statusCredits ?? 0) + (totalPointsCalculation?.data?.bonusStatusCredits ?? 0)

  if (
    !loyaltyEnabled ||
    !calculationRequests ||
    (Array.isArray(calculationRequests) && nonNullable(calculationRequests).length === 0) ||
    err ||
    (!initial && !fetching && !displayedPoints) ||
    (!!accountRegion && accountRegion !== currentRegionCode) ||
    (burnAdjustedTotals?.remainingCash !== undefined && burnAdjustedTotals.remainingCash <= 0)
  ) return null
  return <Container>
    {(fetching || initial) && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size={size} />}
    {!fetching && !initial && <>
      {(!bonusPointsCampaignTypes || hidePromoPill) && <PriceRowLoyaltyPoints
        tier={displayTier}
        earnablePoints={displayedPoints}
        size={size}
        calculationType={calculationType}
        statusCredits={shouldHideStatusCredits ? undefined : displayedStatusCredits}
        pointsEarnRatio={pointsEarnRatio}
        horizontalAlign={horizontalAlign}
        onInfoIconClick={onInfoIconClick}
      />}
      {!!bonusPointsCampaignTypes && !hidePromoPill && <PriceRowLoyaltyPointsPromoPill
        campaigns={bonusPointsCampaignTypes}
        tier={displayTier}
        earnablePoints={displayedPoints}
        size={size}
        calculationType={calculationType}
        statusCredits={shouldHideStatusCredits ? undefined : displayedStatusCredits}
        pointsEarnRatio={pointsEarnRatio}
        horizontalAlign={horizontalAlign}
        trailingCaption={trailingCaption}
        onPillClick={onInfoIconClick}
      />}
    </>}
  </Container>
}

export default _LoyaltyPointsEarn
